import { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import PlusTax from "./PlusTax";
import { useImage } from "../hooks/useImage";

export default function Example() {
  const { t } = useTranslation();
  const container = useRef(null);
  const isInView = useInView(container);
  const animation = useAnimation();

  // 1. Fetch images
  const { imageUrl: img1 } = useImage('d0xnxoDQKSNeDF5WW9UB');
  const { imageUrl: img2 } = useImage('aIsxwr4tL2OAWX7ZHZvb');
  const { imageUrl: img3 } = useImage('zxKHlak43Hjppf640rum');

  // 2. Carousel State
  const [currentIndex, setCurrentIndex] = useState(0);

  // 3. Prepare images
  const validImages = [img1, img2, img3].filter(Boolean);
  const displayImages = validImages.length > 0 ? validImages : ["/assets/img/IMG-20240803-WA0013.jpg"];

  // 4. Timer Logic
  useEffect(() => {
    if (displayImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % displayImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [displayImages.length]);

  useEffect(() => {
    if (isInView) {
      animation.start({
        x: 0,
        opacity: 1,
        transition: { type: "spring", duration: 2, bounce: 0.2 },
      });
    }
  }, [isInView, animation]);

  return (
    <div className="relative bg-custom-beige">
      
      {/* LEFT SIDE (Image Section) - FULL BLEED
         - lg:absolute, lg:inset-y-0, lg:left-0: Pins it to the far left and stretches top-to-bottom.
         - lg:w-1/2: Takes up exactly 50% of the screen width.
      */}
      <div className="relative h-80 overflow-hidden md:h-96 lg:absolute lg:inset-y-0 lg:left-0 lg:h-full lg:w-1/2">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={displayImages[currentIndex]}
            alt="Hotel services"
            
            // Fade Animation
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        
        {/* Optional: Slight gradient overlay to ensure the image edge blends nicely if needed */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent lg:hidden" />
      </div>

      {/* RIGHT SIDE (Content Section)
         - mx-auto max-w-7xl: Standard container logic.
         - lg:grid lg:grid-cols-2: Splits the standard container.
         - lg:col-start-2: Forces text into the right half.
         - lg:px-8: Adds padding so text doesn't touch the image or screen edge.
      */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:grid lg:grid-cols-2 lg:px-8">
        
        {/* Text Wrapper */}
        <div 
          ref={container} 
          className="lg:col-start-2 lg:py-24 lg:pl-16 pb-16 pt-12"
        >
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={animation}
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t("dogHotelSpecialTreatmentTitle")}
              <PlusTax />
            </h2>
            
            <p className="mt-4 font-medium text-gray-700">
              {t("dogHotelSpecialTreatmentSubTitle")}
            </p>
            <p className="mt-4 font-medium text-gray-700">
              {t("dogHotelSpecialTreatmentTitleDescription")}
            </p>
            <p className="mt-2 text-gray-500">
              {t("dogHotelSpecialTreatmentDescription")}
            </p>

            {/* Bullet Points */}
            <div className="mt-6 space-y-4">
              <p className="text-gray-700">
                <span className="font-bold">{t("dogHotelSpecialTreatmentBrachycephalicDogsTitle")}:</span>
                <span className="text-gray-500 ml-1">{t("dogHotelSpecialTreatmentBrachycephalicDogsDescription")}</span>
              </p>

              <p className="text-gray-700">
                <span className="font-bold">{t("dogHotelSpecialTreatmentSeniorDogsTitle")}:</span>
                <span className="text-gray-500 ml-1">{t("dogHotelSpecialTreatmentSeniorDogsDescription")}</span>
              </p>

              <p className="text-gray-700">
                <span className="font-bold">{t("dogHotelSpecialTreatmentShyAnxiousDogsTitle")}:</span>
                <span className="text-gray-500 ml-1">{t("dogHotelSpecialTreatmentShyAnxiousDogsDescription")}</span>
              </p>
            </div>

            {/* Disclaimer Box */}
            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-100">
              <p className="text-red-600 font-bold mb-1">{t("disclaimer")}:</p>
              <p className="text-gray-600 text-sm">{t("noCheckInOutSchedule")}</p>
              <p className="text-gray-600 text-sm">{t("noCheckInOutHolidays")}</p>
            </div>

            {/* Critical Care Section */}
            <div className="mt-10 border-t border-gray-200 pt-10">
              <h3 className="text-3xl font-bold tracking-tight text-gray-900">
                {t("dogHotelCriticalCareTitle")}
                <PlusTax />
              </h3>
              <p className="mt-4 font-medium text-gray-700">
                {t("dogHotelCriticalCareSubTitle")}
              </p>
              <p className="mt-2 text-gray-500 mb-6">
                {t("dogHotelCriticalCareDescription")}
              </p>

              <div className="space-y-4">
                <p className="text-gray-700">
                  <span className="font-bold">{t("dogHotelCriticalCareExpertMedicalSupervisionTitle")}:</span>
                  <span className="text-gray-500 ml-1">{t("dogHotelCriticalCareExpertMedicalSupervisionDescription")}</span>
                </p>
                
                <p className="text-gray-700">
                  <span className="font-bold">{t("dogHotelCriticalCareComfortableRetreatTitle")}:</span>
                  <span className="text-gray-500 ml-1">{t("dogHotelCriticalCareComfortableRetreatDescription")}</span>
                </p>
                
                <p className="text-gray-700">
                  <span className="font-bold">{t("dogHotelCriticalCareTailoredComfortCompanionshipTitle")}:</span>
                  <span className="text-gray-500 ml-1">{t("dogHotelCriticalCareTailoredComfortCompanionshipDescription")}</span>
                </p>

                <p className="text-gray-700">
                  <span className="font-bold">{t("dogHotelCriticalCareAlwaysWatchfulTitle")}:</span>
                  <span className="text-gray-500 ml-1">{t("dogHotelCriticalCareAlwaysWatchfulDescription")}</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}